import dayjs, { Dayjs } from 'dayjs';
import React, { useState } from 'react';
import {
  DateRange,
  DayPicker,
  Matcher,
  rangeIncludesDate,
} from 'react-day-picker';
import 'react-day-picker/style.css'; // Ensure you import the styles

import styled from '@emotion/styled';
import { rgba } from 'emotion-rgba';
import theme from '../../theme';
import { Box } from '../Rebass';

const DatePickerWrapper = styled(Box)`
  color: ${theme.colors.textDark};
`;

interface Dates {
  startDate: Dayjs;
  endDate: Dayjs;
}

interface DatePickerProps {
  startDate: Dayjs;
  endDate: Dayjs;
  setDates: (dates: Dates) => void;
  openDirection: 'up' | 'down';
  variant: 'default' | 'minimal';
  isOpen?: boolean;
  colorScheme?: string;
  disabled?: boolean;
  isOutsideRange?: Matcher | Matcher[] | undefined;
}

const DatePickerContainer = styled(DatePickerWrapper)(({
  variant,
  colorScheme,
}) => {
  const isMinimal = variant === 'minimal';
  return `
    .rdp-root {
      --rdp-font-family: ${isMinimal ? theme.fonts.sans : theme.fonts.serif};
      --rdp-selected-font: normal medium ${
        isMinimal ? theme.fonts.sans : theme.fonts.serif
      };
      --rdp-range_middle-font: normal medium ${
        isMinimal ? theme.fonts.sans : theme.fonts.serif
      };
      --rdp-day_button-border-radius: 0;
      --rdp-accent-color: ${colorScheme};
      --rdp-accent-background-color: ${rgba(colorScheme, 0.8)};
      --rdp-today-color: ${colorScheme};
    }
    .rdp-day_button {
      font: normal medium ${isMinimal ? theme.fonts.sans : theme.fonts.serif}; 
    }
    .rdp-chevron {
       fill: ${theme.colors.textDark};
    }
    .rdp-selected {
      color: white;
    }
    .rdp-month_caption {
      font-family: ${theme.fonts.serif};
      line-height: 22px;
    }
    .DateInput {
      width: ${isMinimal ? '80px' : '130px'};
    }
    .DateInput_input {
      cursor: pointer;
      font-family: ${isMinimal ? theme.fonts.sans : theme.fonts.serif};
      font-size: ${isMinimal ? '14px' : '16px'};
      font-weight: 400;
      ${isMinimal ? 'padding: 0px;' : ''}
      color: ${isMinimal ? theme.colors.textLight : theme.colors.textDark};
    }
    .DateRangePickerInput {
      ${isMinimal ? 'border: none;' : ''}
    }
    .DateInput_input__focused {
      border-bottom: 2px solid ${colorScheme};
    }
    .DayPicker_weekHeader_li {
      font-family: var(--sans);
    }
    .CalendarDay {
      font-family: ${theme.fonts.serif};
    }
    .CalendarDay.CalendarDay__selected_start,
    .CalendarDay__selected_end,
    .CalendarDay__selected:hover,
    .CalendarDay__selected:active {
      background: ${colorScheme};
      border-color: ${colorScheme};
    }
    .CalendarDay__hovered_span:hover,
    .CalendarDay__hovered_span,
    .CalendarDay.CalendarDay__selected_span,
    .CalendarDay__default:hover {
      background: ${colorScheme};
      border-color: ${colorScheme};
      color: ${theme.colors.textWhite};
      opacity: 0.8;
    }
    .DayPicker {
      box-shadow: 0 12px 17px 2px rgba(0, 0, 0, 0.05),
        0 5px 22px 4px rgba(0, 0, 0, 0.06), 0 7px 8px 0 rgba(0, 0, 0, 0.07);
    }
    .DateInput_fang {
      filter: drop-shadow(0px -9px 12px rgba(0, 0, 0, 0.5));
    }
    .DateInput_fangStroke {
      stroke: none;
    }
  `;
});

const DatePicker = (props: DatePickerProps) => {
  const {
    startDate,
    endDate,
    setDates,
    openDirection,
    variant = 'default',
    isOpen,
    colorScheme = theme.colors.brandBrown,
    disabled,
  } = props;

  // Convert initial Dayjs values to Date objects for internal state
  const initialStart = startDate ? dayjs(startDate).toDate() : undefined;
  const initialEnd = endDate ? dayjs(endDate).toDate() : undefined;

  const [selectedRange, setSelectedRange] = useState<DateRange | undefined>({
    from: initialStart,
    to: initialEnd,
  });

  // Track which input is focused ('start' or 'end')
  const [focusedInput, setFocusedInput] = useState<'start' | 'end'>('start');

  // Handle clicks on a day in the calendar
  const handleDayClick = (day: Date) => {
    const clickedDay = dayjs(day);
    const currentStartDate = selectedRange.from
      ? dayjs(selectedRange.from)
      : null;
    const currentEndDate = selectedRange.to ? dayjs(selectedRange.to) : null;
    if (focusedInput === 'start') {
      if (!currentEndDate || clickedDay.isBefore(currentEndDate)) {
        setSelectedRange((previousRange) => ({
          from: day,
          to: previousRange?.to,
        }));
        setDates({
          startDate: clickedDay,
          endDate: selectedRange.to ? dayjs(selectedRange.to) : null,
        });
      }

      if (currentEndDate && clickedDay.isAfter(currentEndDate)) {
        setSelectedRange({ from: day, to: null });
        setDates({ startDate: clickedDay, endDate: null });
      }
      setFocusedInput('end');
    }

    if (focusedInput === 'end') {
      if (clickedDay.isBefore(currentStartDate)) {
        setSelectedRange({ from: day, to: null });
        setDates({ startDate: clickedDay, endDate: null });
      }
      if (clickedDay.isAfter(currentStartDate)) {
        setSelectedRange((previousRange) => ({
          from: previousRange?.from,
          to: day,
        }));
        setDates({
          startDate: selectedRange.from ? dayjs(selectedRange.from) : null,
          endDate: clickedDay,
        });
        setFocusedInput('start');
      }
    }
  };

  return (
    <DatePickerContainer
      variant={variant}
      colorScheme={colorScheme ?? theme.colors.brandBrown}
    >
      <DayPicker
        selected={{ from: selectedRange.from, to: selectedRange.to }}
        onDayClick={handleDayClick}
        defaultMonth={selectedRange.from || new Date()}
        disabled={props.isOutsideRange ? props.isOutsideRange : () => false}
        numberOfMonths={variant === 'minimal' ? 1 : 2}
        modifiers={{
          selected: selectedRange,
          range_start: selectedRange?.from,
          range_end: selectedRange?.to,
          range_middle: (date: Date) =>
            selectedRange
              ? rangeIncludesDate(selectedRange, date, true)
              : false,
        }}
      />
    </DatePickerContainer>
  );
};

export default DatePicker;
